import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  SeverityBadge,
  Spinner,
} from '@aigovops/design-system';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useOptionalWorkspace } from '../lib/workspace';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import { createIncident, incidentLedgerCsvUrl, listIncidents, type IncidentRow } from '../lib/api';
import { fmtDate } from '../lib/format';

/**
 * /w/:slug/app/incidents — Incident intake and tracking.
 *
 * Lists AI-related incidents with severity badges and status. An inline
 * form lets users report new incidents (title, severity, jurisdiction).
 * The createIncident API function will be added when the POST endpoint
 * ships; the form captures the data shape now so the UI is ready.
 */

// The API's vocabulary (V111-0-1): 'moderate' was offered here and always 422'd.
const SEVERITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const STATUS_TONE: Record<string, 'accent' | 'neutral'> = {
  open: 'accent',
  investigating: 'accent',
  resolved: 'neutral',
  closed: 'neutral',
};

export function IncidentIntake() {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? '';
  const canExport = workspaceCtx?.role === 'owner' || workspaceCtx?.role === 'admin';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // The global "New incident" action links here with ?action=new.
  const [formOpen, setFormOpen] = useState(searchParams.get('action') === 'new');
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState('high');
  const [jurisdiction, setJurisdiction] = useState('');

  const listQuery = useQuery({
    queryKey: queryKeys.incidents(slug),
    queryFn: () => listIncidents(slug),
    enabled: !!slug,
  });
  const list = {
    data: listQuery.data ?? null,
    error: listQuery.error?.message ?? null,
    loading: listQuery.isLoading,
    refetch: listQuery.refetch,
  };

  const rows = useMemo(() => list.data?.incidents ?? [], [list.data]);

  const openForm = useCallback(() => {
    setFormOpen(true);
    setTimeout(() => document.getElementById('inc-title')?.focus(), 0);
  }, []);

  const resetForm = useCallback(() => {
    setTitle('');
    setSeverity('high');
    setJurisdiction('');
    setFormError(null);
    setFormOpen(false);
    setTimeout(() => document.getElementById('inc-report-btn')?.focus(), 0);
  }, []);

  const handleReport = useCallback(async () => {
    if (!slug || !title.trim()) return;
    setFormBusy(true);
    setFormError(null);
    try {
      const { incident } = await createIncident(slug, {
        title: title.trim(),
        severity,
        jurisdiction: jurisdiction.trim() || undefined,
      });
      void list.refetch();
      // Next step is the impact details that start the regulator clocks.
      navigate(`/w/${slug}/app/incidents/${encodeURIComponent(incident.incident_id)}#inc-impact`);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setFormBusy(false);
    }
  }, [slug, title, severity, jurisdiction, list, navigate]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Incidents"
        subtitle="AI-related incidents, breaches, and near-misses."
        right={
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {canExport && rows.length > 0 ? (
              <a href={incidentLedgerCsvUrl(slug)} download style={{ alignSelf: 'center', display: 'inline-flex', alignItems: 'center', minHeight: 'var(--control-compact-min-h)', fontSize: 'var(--font-size-2xs)' }}>
                Export loss ledger (CSV)
              </a>
            ) : null}
            {!formOpen ? (
              <Button id="inc-report-btn" variant="primary" compact onClick={openForm}>
                + Report incident
              </Button>
            ) : null}
          </div>
        }
      />

      {formOpen ? (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--surface)',
            padding: 'var(--space-4)',
            display: 'grid',
            gap: 'var(--space-3)',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
            Report incident
          </h2>
          <Field id="inc-title" label="Title">
            <Input
              id="inc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief description of the incident"
            />
          </Field>
          <Field id="inc-severity" label="Severity">
            <Select
              id="inc-severity"
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
            >
              {SEVERITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </Field>
          <Field id="inc-jurisdiction" label="Jurisdiction (optional)">
            <Input
              id="inc-jurisdiction"
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              placeholder="e.g. EU, California, Federal"
            />
          </Field>
          {formError ? <Alert variant="error">{formError}</Alert> : null}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button
              variant="primary"
              compact
              disabled={formBusy || !title.trim()}
              onClick={handleReport}
            >
              {formBusy ? 'Reporting...' : 'Report incident'}
            </Button>
            <Button variant="ghost" compact disabled={formBusy} onClick={resetForm}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {list.loading ? (
        <Spinner label="Loading incidents" />
      ) : list.error ? (
        <Alert variant="error">{list.error}</Alert>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No incidents"
          description="No AI-related incidents have been reported. Use the button above to report one."
          action={
            !formOpen ? (
              <Button variant="primary" compact onClick={openForm}>
                + Report incident
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-3)' }}>
          {rows.map((inc) => (
            <IncidentRow key={inc.incident_id} incident={inc} slug={slug} />
          ))}
        </ol>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function IncidentRow({ incident, slug }: { incident: IncidentRow; slug: string }) {
  const inc = incident;
  return (
    <li
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        display: 'flex',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
      }}
    >
      <div style={{ display: 'grid', gap: '4px', minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <SeverityBadge severity={mapSeverity(inc.severity)} />
          <Link to={`/w/${slug}/app/incidents/${encodeURIComponent(inc.incident_id)}`} style={{ fontWeight: 600, color: 'var(--text)' }}>
            {inc.title}
          </Link>
          <Badge tone={STATUS_TONE[inc.status] ?? 'neutral'}>{inc.status}</Badge>
          {inc.overdue_obligations ? (
            <Link
              to={`/w/${slug}/app/incidents/${encodeURIComponent(inc.incident_id)}#inc-deadlines`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', minHeight: 'var(--control-compact-min-h)',
                padding: '2px var(--space-2)', borderRadius: 'var(--radius-chip)',
                background: 'var(--danger-bg)', color: 'var(--danger-fg)',
                fontSize: 'var(--font-size-3xs)', fontWeight: 600,
              }}
            >
              <span aria-hidden="true">✕</span>
              {inc.overdue_obligations === 1 ? '1 notification overdue' : `${inc.overdue_obligations} notifications overdue`}
            </Link>
          ) : null}
        </div>
        <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
          Reported {fmtDate(inc.reported_at)}
          {inc.jurisdiction ? ` · ${inc.jurisdiction}` : ''}
        </span>
      </div>
    </li>
  );
}

function mapSeverity(s: string): 'critical' | 'high' | 'elevated' | 'moderate' | 'standard' {
  const map: Record<string, 'critical' | 'high' | 'elevated' | 'moderate' | 'standard'> = {
    critical: 'critical',
    high: 'high',
    medium: 'moderate',
    elevated: 'elevated',
    moderate: 'moderate',
    low: 'standard',
    standard: 'standard',
  };
  return map[s] ?? 'standard';
}
